// index.tsx
// Notes component for displaying and adding notes to a case.
// Handles fetching, displaying, and creating notes associated with a case, including UI for note entry and refresh.

import { useState } from "react";
import { useNotes } from "@/hooks/use-notes";
import { useCaseDetails } from "@/hooks/use-case-details";
import Loader from "@/components/Loader";
import { Note } from "@/types/notes";
import { Table, Button, Textarea } from "@Forge/dist";
import { DateTime } from "luxon";
import {
  ProcedureNameWrapper,
  Row,
} from "@/components/CaseDetails/components.stitches";
import { styled } from "@stitches/react";

/**
 * Props for the Notes component
 * @property caseId - The unique identifier for the case to display notes for
 */
interface NoteProps {
  caseId: string;
}

type NotesKey = keyof Note;

interface Column {
  key: NotesKey;
  displayName: string;
  template?: (value: string) => string;
}

/**
 * Wrapper for the note entry form.
 */
const FormWrapper = styled("div", {
  margin: "10px 0",
  display: "flex",
  // alignItems: "center",
  gap: 12,
  "& > button": {
    width: 135,
  },
});

/**
 * Table columns for displaying notes.
 */
const columns: Column[] = [
  {
    key: "actionNote",
    displayName: "Action Note",
  },
  {
    key: "requestedUser",
    displayName: "User",
  },
  {
    key: "timeStamp",
    displayName: "Time Stamp",
    template: (date: string) => DateTime.fromISO(date + "Z").toFormat("fff"),
  },
];

/**
 * Notes Component
 * Displays a list of notes for a given case and provides a form to add new notes.
 * Handles loading, error, and refresh states for both case details and notes.
 * @param caseId - The unique identifier for the case
 */
const Notes = ({ caseId }: NoteProps) => {
  const [note, setNote] = useState("");

  // Fetch case details for header display
  const {
    get: {
      data: caseDetails,
      isLoading: isLoadingCaseDetails,
      isError: isCaseDetailsError,
      error: caseDetailsError,
    },
  } = useCaseDetails(caseId);

  // Fetch and create notes for the case
  const {
    get: { data, isLoading, isError, error, refetch, isFetching },
    post: { createNote, isPending: isPendingNoteCreation },
  } = useNotes(caseId);

  /**
   * Refreshes the notes list by refetching from the server.
   */
  const refresh = () => {
    refetch()
      .then((res) => res)
      .catch((err) => console.log(err));
  };

  /**
   * Handles creating a new note and clearing the input.
   */
  const handleCreateNote = () => {
    createNote(note);
    setNote("");
  };

  return (
    <Loader
      displayText="Getting the case details..."
      isLoading={isLoadingCaseDetails}
      isError={isCaseDetailsError}
      errorReason={caseDetailsError?.message}
    >
      <ProcedureNameWrapper>
        <Row spaceBetween>
          <h2>
            {caseDetails?.procedureInformation.updatedProcedureName ??
              `Scheduled as: ${
                caseDetails?.procedureInformation.scheduledprocedureName ??
                "No Procedure Name Available"
              }`}
          </h2>
          <Button
            variant="secondary"
            icon="Refresh"
            onClick={refresh}
            text="Refresh"
          />
        </Row>
        <Row>
          {caseDetails?.procedureInformation.updatedProcedureName && (
            <h4>
              Scheduled as:{" "}
              {caseDetails?.procedureInformation.scheduledprocedureName}
            </h4>
          )}
        </Row>
      </ProcedureNameWrapper>
      <FormWrapper>
        <Textarea
          placeholder="Write your note here..."
          value={note}
          disabled={isPendingNoteCreation}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNote(e.target.value)
          }
        />
        <Button
          disabled={!note || isPendingNoteCreation}
          type="button"
          size="small"
          onClick={handleCreateNote}
        >
          {isPendingNoteCreation ? "Adding Note..." : "Add Note"}
        </Button>
      </FormWrapper>

      <Loader
        displayText="Getting the latest notes..."
        isLoading={isLoading || isFetching}
        isError={isError}
        errorReason={error?.message}
      >
        <Table
          nowrapHeaders
          layout="compact"
          rowKey="timeStamp"
          columns={columns}
          rows={data ?? []}
        />
      </Loader>
    </Loader>
  );
};

export default Notes;
